import api from "@/api/client";
import { LOCATION_REQUIRED } from "@/location/locationPolicy";
import { v4 as uuid } from "uuid";
import { create } from "zustand";
import { persist } from "zustand/middleware";

export type LocationPermission = "unknown" | "prompt" | "granted" | "denied" | "blocked" | "unsupported";

export type CapturedLocation = {
  latitude: number;
  longitude: number;
  accuracy: number | null;
  capturedAt: string;
  method: "high" | "fallback" | "last_known";
};

export type LocationRequestResult =
  | { state: "granted"; location: CapturedLocation }
  | { state: "declined" | "denied" | "blocked" | "unsupported" | "error"; error?: string };

type Resolver = (result: LocationRequestResult) => void;

type LocationPingPayload = {
  context: "search" | "note";
  requestId: string;
  udprn?: number;
  location: CapturedLocation;
  endpointOverride?: string;
  method?: "post" | "patch";
};

interface LocationState {
  status: LocationPermission;
  userDeclined: boolean;
  promptOpen: boolean;
  promptReason: string | null;
  lastLocation: CapturedLocation | null;
  pendingResolvers: Resolver[];
  bootstrap: () => Promise<void>;
  requestLocation: (reason?: string) => Promise<LocationRequestResult>;
  respondToPrompt: (action: "allow" | "deny") => Promise<void>;
  clearDecline: () => void;
  sendLocationPing: (payload: LocationPingPayload) => Promise<void>;
}

const storageKey = "location-permission-prefs";

const mapPermission = (value?: PermissionState | null): LocationPermission => {
  if (!value) return "unknown";
  if (value === "granted") return "granted";
  if (value === "denied") return "denied";
  return "prompt";
};

const getPosition = async (
  options: PositionOptions,
  method: CapturedLocation["method"]
): Promise<LocationRequestResult> => {
  if (!("geolocation" in navigator)) {
    return { state: "unsupported", error: "Geolocation not available" };
  }

  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        resolve({
          state: "granted",
          location: {
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
            accuracy: typeof pos.coords.accuracy === "number" ? pos.coords.accuracy : null,
            capturedAt: new Date(pos.timestamp || Date.now()).toISOString(),
            method,
          },
        });
      },
      (err) => {
        if (err.code === err.PERMISSION_DENIED) {
          resolve({ state: "denied", error: err.message });
        } else {
          resolve({ state: "error", error: err.message });
        }
      },
      options
    );
  });
};

async function fetchWithFallback(): Promise<LocationRequestResult> {
  const primary = await getPosition(
    {
      enableHighAccuracy: true,
      timeout: 10_000,
      maximumAge: 2_000,
    },
    "high"
  );

  if (primary.state === "granted" || primary.state === "denied") {
    return primary;
  }

  const fallback = await getPosition(
    {
      enableHighAccuracy: false,
      timeout: 5_000,
      // Fresh reading only; avoid cached points for accuracy
      maximumAge: 5_000,
    },
    "fallback"
  );

  return fallback;
}

export const makeClientRequestId = () => (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : uuid());

export const locationToPayload = (location: CapturedLocation) => ({
  latitude: location.latitude,
  longitude: location.longitude,
  accuracy: location.accuracy,
  captured_at: location.capturedAt,
  method: location.method,
});

export const buildLocationHeaders = (location?: CapturedLocation | null, requestId?: string | null) => {
  const headers: Record<string, string> = {};
  if (requestId) headers["X-Client-Request-Id"] = requestId;

  if (location) {
    headers["X-Geo-Lat"] = String(location.latitude);
    headers["X-Geo-Lng"] = String(location.longitude);
    if (location.accuracy !== null && location.accuracy !== undefined) {
      headers["X-Geo-Accuracy"] = String(location.accuracy);
    }
    headers["X-Geo-Captured-At"] = location.capturedAt;
    headers["X-Geo-Method"] = location.method;
  }

  return headers;
};

export const useLocationStore = create<LocationState>()(
  persist(
    (set, get) => ({
      status: "unknown",
      userDeclined: false,
      promptOpen: false,
      promptReason: null,
      lastLocation: null,
      pendingResolvers: [],

      async bootstrap() {
        if (get().status !== "unknown") return;
        if (!("geolocation" in navigator)) {
          set({ status: "unsupported" });
          return;
        }

        try {
          if ("permissions" in navigator && typeof navigator.permissions.query === "function") {
            const perm = await navigator.permissions.query({ name: "geolocation" as PermissionName });
            set({ status: mapPermission(perm.state) });
            perm.onchange = () => set({ status: mapPermission(perm.state) });
          } else {
            set({ status: "prompt" });
          }
        } catch {
          set({ status: "prompt" });
        }
      },

      async requestLocation(reason) {
        await get().bootstrap();
        const status = get().status;

        console.debug("requestLocation", status)

        if (status === "unsupported") {
          return { state: "unsupported", error: "Geolocation not available" };
        }

        if (status === "denied" || status === "blocked") {
          return { state: "denied", error: "Permission denied" };
        }

        if (!LOCATION_REQUIRED && get().userDeclined && status !== "granted") {
          return { state: "declined" };
        }

        if (status === "granted") {
          const res = await fetchWithFallback();
          if (res.state === "granted") {
            set({ lastLocation: res.location, status: "granted", userDeclined: false });
          }
          return res;
        }

        return new Promise<LocationRequestResult>((resolve) => {
          set((state) => ({
            promptOpen: true,
            promptReason: reason ?? null,
            pendingResolvers: [...state.pendingResolvers, resolve],
          }));
        });
      },

      async respondToPrompt(action) {
        const resolvers = get().pendingResolvers;
        set({ promptOpen: false, promptReason: null, pendingResolvers: [] });

        if (action === "deny") {
          set({ userDeclined: LOCATION_REQUIRED ? false : true });
          resolvers.forEach((r) => r({ state: "declined" }));
          return;
        }

        const res = await fetchWithFallback();
        if (res.state === "granted") {
          set({ status: "granted", userDeclined: false, lastLocation: res.location });
        } else if (res.state === "denied") {
          set({ status: "denied", userDeclined: LOCATION_REQUIRED ? false : true });
        }

        resolvers.forEach((r) => r(res));
      },

      clearDecline() {
        set({ userDeclined: false });
      },

      async sendLocationPing({ context, requestId, udprn, location, endpointOverride, method }) {
        try {
          const config: { suppressGlobalErrorToast: boolean } = { suppressGlobalErrorToast: true };
          const path =
            endpointOverride ??
            (context === "search"
              ? `searches/${requestId}/location/`
              : "client-location/");

          await api.request({
            url: path,
            method: method ?? "post",
            data: {
              context,
              request_id: requestId,
              udprn: udprn ?? null,
              location: locationToPayload(location),
            },
            ...config,
          });
        } catch (err) {
          // Silently ignore; this is best-effort telemetry
          console.debug("Location ping failed", err);
        }
      },
    }),
    {
      name: storageKey,
      partialize: (state) => ({
        userDeclined: state.userDeclined,
      }),
    }
  )
);
